import { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, Loader2, Search, ExternalLink, LayoutList } from 'lucide-react';
import { PageSelector } from '../../components/contenido-publico/PageSelector';
import { ContenidoPublicoForm } from '../../components/contenido-publico/ContenidoPublicoForm';
import { ContenidoPublicoList } from '../../components/contenido-publico/ContenidoPublicoList';
import { getFieldMeta } from '../../components/contenido-publico/field-labels';
import { contenidoPublicoService } from '../../services/contenido-publico/contenido-publico.service';
import type {
  ContenidoPublico,
  ContenidoPublicoGrouped,
  PaginaPublica,
  TipoContenidoPublico,
} from '../../types/contenido-publico';

const TODAS_LAS_PAGINAS: PaginaPublica[] = [
  'INICIO',
  'NOSOTROS',
  'EVENTOS',
  'CONTACTO',
  'DONACIONES',
  'FUNDACION',
  'IGLESIA',
  'HEADER',
  'FOOTER',
];

// Rutas públicas del sitio (para "ver página")
const RUTAS_PUBLICAS: Partial<Record<PaginaPublica, string>> = {
  INICIO: '/',
  NOSOTROS: '/nosotros',
  EVENTOS: '/eventos',
  IGLESIA: '/iglesia',
  CONTACTO: '/contacto',
  DONACIONES: '/donaciones',
  FUNDACION: '/fundacion',
};

const ContenidoPublicoPage: React.FC = () => {
  const [pagina, setPagina] = useState<PaginaPublica>('INICIO');
  const [content, setContent] = useState<ContenidoPublicoGrouped>({});
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ContenidoPublico | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [counts, setCounts] = useState<Partial<Record<PaginaPublica, number>>>({});

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contenidoPublicoService.findAll({ pagina });
      const grouped: ContenidoPublicoGrouped = {};
      data.forEach((item) => {
        if (!grouped[item.seccion]) grouped[item.seccion] = [];
        grouped[item.seccion].push(item);
      });
      setContent(grouped);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando el contenido');
    } finally {
      setLoading(false);
    }
  }, [pagina]);

  // Contadores de campos por página (para las tarjetas del selector)
  const fetchCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        TODAS_LAS_PAGINAS.map((p) =>
          contenidoPublicoService.findAll({ pagina: p }).catch(() => [] as ContenidoPublico[]),
        ),
      );
      const newCounts: Partial<Record<PaginaPublica, number>> = {};
      TODAS_LAS_PAGINAS.forEach((p, i) => {
        newCounts[p] = results[i].length;
      });
      setCounts(newCounts);
    } catch {
      // Silencioso: los contadores son un extra
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Secciones filtradas por búsqueda y ordenadas como aparecen en la página
  const filteredContent = useMemo(() => {
    const allItems = Object.values(content).flat();
    const q = search.trim().toLowerCase();

    const filtered = q
      ? allItems.filter((item) => {
          const meta = getFieldMeta(pagina, item.seccion, item.clave);
          return (
            item.clave.toLowerCase().includes(q) ||
            item.valor.toLowerCase().includes(q) ||
            item.seccion.toLowerCase().includes(q) ||
            meta.label.toLowerCase().includes(q)
          );
        })
      : allItems;

    const grouped: ContenidoPublicoGrouped = {};
    filtered.forEach((item) => {
      if (!grouped[item.seccion]) grouped[item.seccion] = [];
      grouped[item.seccion].push(item);
    });

    // Ordenar secciones según la posición del primer campo (orden real en la página)
    const entries = Object.entries(grouped).sort(
      ([, a], [, b]) => Math.min(...a.map((i) => i.orden)) - Math.min(...b.map((i) => i.orden)),
    );
    return Object.fromEntries(entries) as ContenidoPublicoGrouped;
  }, [content, search, pagina]);

  const secciones = Object.keys(filteredContent);
  const buscando = search.trim().length > 0;

  const handleSubmit = async (data: {
    pagina: PaginaPublica;
    seccion: string;
    clave: string;
    tipo: TipoContenidoPublico;
    valor: string;
    orden: number;
    activo: boolean;
    metadata?: string;
  }) => {
    try {
      if (editing) {
        await contenidoPublicoService.update(editing.id, data);
      } else {
        await contenidoPublicoService.create(data);
      }
      setShowForm(false);
      setEditing(undefined);
      await fetchContent();
      fetchCounts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error guardando el contenido');
    }
  };

  const handleEdit = (item: ContenidoPublico) => {
    setEditing(item);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este contenido?')) return;
    try {
      await contenidoPublicoService.delete(id);
      await fetchContent();
      fetchCounts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error eliminando el contenido');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await contenidoPublicoService.toggleActivo(id);
      await fetchContent();
      fetchCounts();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cambiando el estado');
    }
  };

  const handleNew = () => {
    setEditing(undefined);
    setShowForm(true);
  };

  const handlePageChange = (p: PaginaPublica) => {
    setPagina(p);
    setShowForm(false);
    setEditing(undefined);
    setSearch('');
    setOpenSections({});
  };

  const handleToggleSection = (seccion: string) => {
    setOpenSections((prev) => ({ ...prev, [seccion]: !(prev[seccion] ?? false) }));
  };

  const jumpToSection = (seccion: string) => {
    setOpenSections((prev) => ({ ...prev, [seccion]: true }));
    requestAnimationFrame(() => {
      document.getElementById(`cms-section-${seccion}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const rutaPublica = RUTAS_PUBLICAS[pagina];

  return (
    <div className="p-6 w-full">
      {/* Página */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-gray-700">Página</label>
          {rutaPublica && (
            <a
              href={rutaPublica}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Ver página pública
            </a>
          )}
        </div>
        <PageSelector value={pagina} onChange={handlePageChange} counts={counts} />
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Buscador */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nombre del campo, clave o texto... (expande automáticamente las coincidencias)"
          className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* Índice de secciones */}
      {secciones.length > 0 && (
        <div className="mb-6">
          <p className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-2">
            <LayoutList className="w-3.5 h-3.5" />
            Estructura de la página {buscando && `· ${secciones.length} secciones con coincidencias`}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {secciones.map((seccion) => (
              <button
                key={seccion}
                onClick={() => jumpToSection(seccion)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:text-purple-700 transition-colors"
                title="Ir a la sección"
              >
                {seccion}
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-semibold">
                  {filteredContent[seccion].length}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mb-6 flex justify-end">
        <button
          onClick={handleNew}
          className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar contenido
        </button>
      </div>

      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowForm(false);
              setEditing(undefined);
            }
          }}
        >
          <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <ContenidoPublicoForm
              pagina={pagina}
              initialData={editing}
              onSubmit={handleSubmit}
              onCancel={() => { setShowForm(false); setEditing(undefined); }}
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <ContenidoPublicoList
          pagina={pagina}
          content={filteredContent}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggle={handleToggle}
          openSections={openSections}
          onToggleSection={handleToggleSection}
          expandAll={buscando}
        />
      )}
    </div>
  );
};

export default ContenidoPublicoPage;
